/**
 * A readable message from an API failure, ALWAYS as a string.
 *
 * THE DEFECT THIS EXISTS FOR. The usual `e?.response?.data?.detail || e?.message` puts whatever
 * FastAPI sent straight into JSX, and `detail` is only sometimes a string:
 *
 *   * a validation failure (422) sends a LIST of `{loc, msg, type}`;
 *   * our own structured refusals send an OBJECT, e.g. `{errors: [...], warnings: [...]}`.
 *
 * Rendering either throws "Objects are not valid as a React child", which is not caught next to the
 * message — it unmounts the tree and the whole page goes BLANK. So an error message, the thing shown
 * when something already went wrong, took the page down with it and hid the reason.
 *
 * Everything here is therefore about never returning a non-string, including for shapes nobody has
 * seen yet: an unrecognised object is JSON, which is ugly and readable, rather than a crash.
 */
export function errorText(e: any, fallback = 'Something went wrong.'): string {
  const detail = e?.response?.data?.detail

  const fromDetail = (d: any): string | null => {
    if (d == null) return null
    if (typeof d === 'string') return d.trim() || null
    if (Array.isArray(d)) {
      // FastAPI's 422 shape. `msg` alone reads as "field required" with no field, so the last path
      // segment is kept when there is one — `body.template_id: field required`.
      const parts = d
        .map((item) => {
          if (typeof item === 'string') return item
          const msg = item?.msg ?? item?.message
          if (!msg) return JSON.stringify(item)
          const loc = Array.isArray(item?.loc) ? item.loc[item.loc.length - 1] : null
          return loc ? `${loc}: ${msg}` : String(msg)
        })
        .filter(Boolean)
      return parts.length ? parts.join('; ') : null
    }
    if (typeof d === 'object') {
      // Our structured refusals. The warnings are deliberately not shown: the errors are why the
      // request failed, and a warning beside them reads as a second failure.
      const errors = d.errors ?? d.error ?? d.message ?? d.detail
      if (errors) return fromDetail(errors)
      return JSON.stringify(d)
    }
    return String(d)
  }

  // `??` only falls through on null/undefined, so each candidate has to BE null when absent — a
  // `&&` chain here yields `false`, and `false ?? fallback` is `false`, which is exactly the
  // non-string this function exists to never return.
  const fromMessage =
    typeof e?.message === 'string' && e.message.trim() ? e.message : null
  return fromDetail(detail) ?? fromMessage ?? fallback
}
